import * as React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import ContactCtaPrototype from '/react4xp/components/prototype/ContactCta'
import type { ContactCtaFormData, ContactPerson } from '/react4xp/components/prototype/ContactCta/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

type ContactCtaPartData = {
	isConfigured?: boolean
	heading?: string
	talkToPrefix?: string
	person?: {
		name?: string
		email?: string
		phone?: string
		image?: { src?: string; alt?: string }
	}
	accordionLabel?: string
	// Which of the part's two form sources the editor picked: 'enonic' for the form
	// below, 'superoffice' for a pasted SuperOffice snippet rendered in its place.
	// Absent on a block saved before the field existed.
	formSource?: string
	superOfficeScript?: string
	privacyUrl?: string
	formSubmitUrl?: string
	contentId?: string
	componentPath?: string
	strings?: {
		nameLabel?: string
		emailLabel?: string
		phoneLabel?: string
		messageLabel?: string
		consentLabel?: string
		consentLinkLabel?: string
		submitLabel?: string
		newMessageLabel?: string
		errorLabel?: string
		emailPillLabel?: string
		phonePillLabel?: string
		requiredFieldMessage?: string
		consentRequiredMessage?: string
		invalidEmailMessage?: string
	}
}

/**
 * Runs a snippet pasted by an editor inside `container`.
 *
 * `dangerouslySetInnerHTML` parses `<script>` tags but never executes them, so each one
 * has to be re-created as a real element for the browser to fetch and run it. Attributes
 * are copied over as authored (`src`, `defer`, `async`, ids, data-*), and any inline body
 * is carried across too, so a snippet mixing markup and script behaves as it would on a
 * plain HTML page.
 */
function runSnippet(container: HTMLElement, snippet: string) {
	const template = document.createElement('template')
	template.innerHTML = snippet
	container.appendChild(template.content)

	container.querySelectorAll('script').forEach((original) => {
		const script = document.createElement('script')
		for (const { name, value } of Array.from(original.attributes)) script.setAttribute(name, value)
		script.text = original.text
		original.replaceWith(script)
	})
}

/**
 * Editor-facing only, so it stays English and hardcoded here - nothing under
 * `react4xp/components/**` localises.
 */
function emptyReason(isConfigured?: boolean, formSource?: string): string {
	if (!isConfigured) {
		return formSource ? 'No contact person selected.' : 'No contact person and no form source selected.'
	}
	return 'The form endpoint could not be resolved.'
}

export const ContactCta = ({ meta, data }: ComponentProps<PartData>) => {
	const {
		isConfigured,
		heading,
		talkToPrefix,
		person,
		accordionLabel,
		formSource,
		superOfficeScript,
		privacyUrl,
		formSubmitUrl,
		contentId,
		componentPath,
		strings = {},
	} = (data || {}) as ContactCtaPartData

	const isSuperOffice = formSource === 'superoffice'
	const isEnonicForm = formSource === 'enonic'

	const superOfficeRef = React.useRef<HTMLDivElement | null>(null)
	const superOfficeRanRef = React.useRef(false)

	// Deliberately not a mount effect. The accordion panel is `h-0 overflow-hidden`
	// until the visitor opens it, and a SuperOffice form sizes itself against the
	// container it lands in - injected on mount it measures a collapsed box and
	// renders clipped once the panel expands. The prototype's `onOpen` fires after
	// the open animation, the first moment the panel is at its content height.
	//
	// `onOpen` fires on every open, and the snippet's own script keeps state of its
	// own, so the ref keeps this to one run per mount: a second run would leave two
	// SuperOffice forms in the container.
	const injectSuperOffice = React.useCallback(() => {
		if (!superOfficeScript || !superOfficeRef.current || superOfficeRanRef.current) return
		superOfficeRanRef.current = true
		runSnippet(superOfficeRef.current, superOfficeScript)
	}, [superOfficeScript])

	// The block is only unrenderable without a contact person - there is no submit url
	// in SuperOffice mode and none on a pre-change block, so a missing one is a fault
	// inside the Enonic branch alone and is reported as such. A picked contact person
	// renders whatever the form source is, so a pre-change block keeps its heading,
	// portrait, "Talk to" line and pills on a published page.
	if (!isConfigured || (isEnonicForm && !formSubmitUrl)) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="contact-cta"
				title="Contact CTA"
				heading={heading}
				reason={emptyReason(isConfigured, formSource)}
			/>
		)
	}

	const contactPerson: ContactPerson | undefined = person
		? {
				name: person.name,
				email: person.email,
				phone: person.phone,
				portrait: person.image?.src ? { src: person.image.src, alt: person.image.alt || '' } : undefined,
			}
		: undefined

	// The Employee block is shared chrome, so both non-Enonic branches keep passing the
	// person, accordion label and pill labels - only the accordion body changes.
	const sharedProps = {
		heading,
		contactPerson,
		accordionLabel,
		labels: {
			talkToPrefix,
			emailPillLabel: strings.emailPillLabel,
			phonePillLabel: strings.phonePillLabel,
		},
	}

	// SuperOffice mode: formSlot takes the place of our fields, consent checkbox and
	// submit row. The container is left empty on the server - the snippet only runs in
	// the browser.
	if (isSuperOffice) {
		return <ContactCtaPrototype {...sharedProps} onOpen={injectSuperOffice} formSlot={<div ref={superOfficeRef} />} />
	}

	// No form source picked yet (a block saved before the field existed). An empty
	// fragment rather than `undefined`, because the prototype nullish-coalesces
	// `formSlot` and would otherwise resurrect its own form with no receiver behind it.
	if (!isEnonicForm) {
		return <ContactCtaPrototype {...sharedProps} formSlot={<></>} />
	}

	const handleSubmit = async (formData: ContactCtaFormData): Promise<boolean> => {
		const body = new FormData()
		body.set('contentId', contentId || '')
		body.set('componentPath', componentPath || '')
		body.set('userName', formData.name)
		body.set('userEmail', formData.email)
		body.set('userPhone', formData.phone || '')
		body.set('userMessage', formData.message || '')
		body.set('consent', formData.consent ? 'true' : 'false')

		// The prototype has no error surface yet, so a rejected send only leaves
		// the button on its "Send" label - log it so a failure is diagnosable.
		try {
			const response = await fetch(formSubmitUrl!, { method: 'POST', body })
			if (!response.ok) console.error('ContactCta: sendFormEmail responded ' + response.status)
			return response.ok
		} catch (error) {
			console.error('ContactCta: sendFormEmail request failed', error)
			return false
		}
	}

	return (
		<ContactCtaPrototype
			heading={heading}
			contactPerson={contactPerson}
			accordionLabel={accordionLabel}
			privacyLink={privacyUrl ? { url: privacyUrl, isExternal: false } : undefined}
			labels={{
				talkToPrefix,
				emailPillLabel: strings.emailPillLabel,
				phonePillLabel: strings.phonePillLabel,
				nameLabel: strings.nameLabel,
				emailLabel: strings.emailLabel,
				phoneLabel: strings.phoneLabel,
				messageLabel: strings.messageLabel,
				consentPrefix: strings.consentLabel,
				consentLinkLabel: strings.consentLinkLabel,
				sendButtonLabel: strings.submitLabel,
				newMessageButtonLabel: strings.newMessageLabel,
				nameRequiredMessage: strings.requiredFieldMessage,
				emailRequiredMessage: strings.requiredFieldMessage,
				consentRequiredMessage: strings.consentRequiredMessage,
				invalidEmailMessage: strings.invalidEmailMessage,
			}}
			onSubmit={handleSubmit}
		/>
	)
}
